import { useCallback } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import type { NetworkStorageParams } from '../../../../common/types';
import { useOptions } from '../../../options-provider';

import type { useNavSettingsService$key } from './__generated__/useNavSettingsService.graphql';
import type {
	useNavSettingsServiceMutation,
	useNavSettingsServiceMutation$variables,
} from './__generated__/useNavSettingsServiceMutation.graphql';

export type UseNavSettingsServiceParams = Omit<NetworkStorageParams, 'entityAri' | 'queryRef'> & {
	mutationEntityAri: string;
	queryRef: useNavSettingsService$key | null | undefined;
};

export function useNavSettingsService(params: UseNavSettingsServiceParams) {
	const { mutationEntityAri: entityAri, ownerAri, queryRef } = params;
	const queryData = useLocalFragment(queryRef);
	const mutation = useLocalMutation();
	const { fireEvent } = useAnalyticsEvents();
	const { customizationStore } = useOptions();

	const fireMutationEvent = useCallback(
		(success: boolean, error?: string) => {
			switch (success) {
				case true:
					fireEvent('operational.executeMutation.succeeded', {
						storage: customizationStore ?? 'network',
					});
					break;
				case false:
					fireEvent('operational.executeMutation.failed', {
						storage: customizationStore ?? 'network',
						error: error ?? 'Unknown error',
					});
					break;
			}
		},
		[customizationStore, fireEvent],
	);

	const setNavData = useCallback(
		({
			sidebar,
			properties,
		}: Pick<useNavSettingsServiceMutation$variables, 'sidebar' | 'properties'>) => {
			executeMutation({
				mutation,
				variables: { entityAri, ownerAri, properties, sidebar },
				fireEvent: fireMutationEvent,
			});
		},
		[entityAri, fireMutationEvent, mutation, ownerAri],
	);

	return { queryData, setNavData };
}

function useLocalFragment(queryRef: UseNavSettingsServiceParams['queryRef']) {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	return useFragment<useNavSettingsService$key>(
		graphql`
			fragment useNavSettingsService on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
				properties {
					nodes {
						key
						value
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
}

function useLocalMutation() {
	return useMutation<useNavSettingsServiceMutation>(graphql`
		mutation useNavSettingsServiceMutation(
			$entityAri: ID!
			$ownerAri: ID!
			$sidebar: [SettingsMenuItemInput]
			$properties: [SettingsDisplayPropertyInput]
		) {
			settings_updateNavigationCustomisation(
				input: {
					entityAri: $entityAri
					ownerAri: $ownerAri
					properties: $properties
					sidebar: $sidebar
				}
			) {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
				properties {
					nodes {
						key
						value
					}
				}
			}
		}
	`)[0];
}

type ExecuteMutationParams = {
	fireEvent: (success: boolean, error?: string) => void;
	mutation: ReturnType<typeof useLocalMutation>;
	variables: useNavSettingsServiceMutation$variables;
};

function executeMutation({ mutation, variables, fireEvent }: ExecuteMutationParams) {
	try {
		return mutation({
			variables: variables,
			onCompleted: () => {
				fireEvent(true);
			},
			onError: (error: Error) => {
				fireEvent(false, error.message || 'Unknown error');
			},
		});
	} catch (error: unknown) {
		fireEvent(false, `Unhandled error: ${(error as Error).message || 'Unknown error'}`);
	}
}
